import { useEffect, useState } from 'react';
import {
  commitMigrationRun,
  confirmMigrationMapping,
  correctMigrationExceptionSignoff,
  getMigrationReconciliation,
  getMigrationRunDetail,
  listMigrationRuns,
  rollbackMigrationCheckpoint,
  signoffMigrationException,
  suggestMigrationMapping,
} from '../../api/migration';
import { usePermissions } from '../../context/PermissionContext';
import {
  Button,
  ClickableRow,
  Drawer,
  DrawerField,
  EmptyState,
  ErrorNote,
  GroupLabel,
  HintNote,
  LoadingNote,
  PageHeader,
  StatusBadge,
  formatDateTime,
  tableStyle,
  tdStyle,
  thStyle,
} from '../../components/common';
import type { MigrationExceptionRecord, MigrationMapping, MigrationReconciliationRow, MigrationRunDetail, MigrationRunSummary, UUID } from '../../types/domain';

/**
 * Migration Rehearsal dashboard (FASE 15, migrations 0059-0061). Every run
 * here targets an isolated, freshly-created clean-target tenant — never a
 * live operational table. This is `/migration/runs` +
 * `/migration/reconciliation` as real permission-checked RPCs
 * (fn_migration_*_checked), TENANT_OWNER-only server-side.
 */
export default function MigrationDashboardPage() {
  const { hasPermission } = usePermissions();
  const canRead = hasPermission('migration', 'read');
  const canApprove = hasPermission('migration', 'approve');
  const canSignoff = hasPermission('migration', 'signoff');

  const [runs, setRuns] = useState<MigrationRunSummary[] | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [openRunId, setOpenRunId] = useState<UUID | null>(null);

  async function reload() {
    if (!canRead) return;
    try {
      setRuns(await listMigrationRuns());
      setError(null);
    } catch (e) {
      setError(e instanceof Error ? e.message : String(e));
    }
  }

  useEffect(() => {
    reload();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [canRead]);

  return (
    <div>
      <PageHeader
        title="Migration Rehearsal"
        subtitle="Dry-run migrasi data ke tenant clean-target yang terisolasi — tidak pernah menulis ke tabel operasional live. Setiap baris sumber punya jejak provenance penuh (ledger, mapping, rekonsiliasi, pengecualian, checkpoint rollback)."
      />
      {!canRead && <HintNote text="Peran Anda tidak memiliki izin migration.read untuk melihat dasbor ini." />}
      {error && <ErrorNote text={error} />}
      {canRead && runs === null && !error && <LoadingNote />}
      {canRead && runs !== null && runs.length === 0 && <EmptyState text="Belum ada migration run — belum ada rehearsal yang dijalankan untuk tenant ini." />}
      {canRead && runs !== null && runs.length > 0 && (
        <table style={tableStyle}>
          <thead>
            <tr>
              <th style={thStyle}>Status</th>
              <th style={thStyle}>Baris</th>
              <th style={thStyle}>Valid</th>
              <th style={thStyle}>Diimpor</th>
              <th style={thStyle}>Duplikat</th>
              <th style={thStyle}>Orphan</th>
              <th style={thStyle}>Ambigu</th>
              <th style={thStyle}>Ditolak</th>
              <th style={thStyle}>Mapping Tertunda</th>
              <th style={thStyle}>Pengecualian Terbuka</th>
              <th style={thStyle}>Checkpoint</th>
              <th style={thStyle}>Mulai</th>
            </tr>
          </thead>
          <tbody>
            {runs.map((r) => (
              <ClickableRow key={r.migration_run_id} onClick={() => setOpenRunId(r.migration_run_id)}>
                <td style={tdStyle}>
                  <StatusBadge status={r.status} />
                </td>
                <td style={tdStyle}>{r.row_count}</td>
                <td style={tdStyle}>{r.valid_count}</td>
                <td style={tdStyle}>{r.imported_count}</td>
                <td style={tdStyle}>{r.duplicate_count}</td>
                <td style={tdStyle}>{r.orphan_count}</td>
                <td style={tdStyle}>{r.ambiguous_count}</td>
                <td style={tdStyle}>{r.rejected_count}</td>
                <td style={tdStyle}>{r.pending_mappings > 0 ? <StatusBadge status="PENDING" /> : '—'}</td>
                <td style={tdStyle}>{r.open_exceptions > 0 ? r.open_exceptions : '—'}</td>
                <td style={tdStyle}>{r.rollback_checkpoint_id ? 'Ada' : '—'}</td>
                <td style={tdStyle}>{formatDateTime(r.started_at)}</td>
              </ClickableRow>
            ))}
          </tbody>
        </table>
      )}

      {openRunId && (
        <MigrationRunDrawer
          migrationRunId={openRunId}
          open={!!openRunId}
          onClose={() => setOpenRunId(null)}
          canApprove={canApprove}
          canSignoff={canSignoff}
          onChanged={reload}
        />
      )}
    </div>
  );
}

function MigrationRunDrawer({
  migrationRunId,
  open,
  onClose,
  canApprove,
  canSignoff,
  onChanged,
}: {
  migrationRunId: UUID;
  open: boolean;
  onClose: () => void;
  canApprove: boolean;
  canSignoff: boolean;
  onChanged: () => void;
}) {
  const [detail, setDetail] = useState<MigrationRunDetail | null>(null);
  const [reconciliation, setReconciliation] = useState<MigrationReconciliationRow[] | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  async function reload() {
    try {
      const [d, r] = await Promise.all([getMigrationRunDetail(migrationRunId), getMigrationReconciliation(migrationRunId)]);
      setDetail(d);
      setReconciliation(r);
      setError(null);
    } catch (e) {
      setError(e instanceof Error ? e.message : String(e));
    }
  }

  useEffect(() => {
    reload();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [migrationRunId]);

  async function handleMapping(mappingId: UUID, decision: 'CONFIRMED' | 'REJECTED', resolvedValue: string | null) {
    setBusy(true);
    try {
      await confirmMigrationMapping(mappingId, decision, resolvedValue);
      await reload();
    } catch (e) {
      setError(e instanceof Error ? e.message : String(e));
    } finally {
      setBusy(false);
    }
  }

  async function handleSuggest(mappingId: UUID) {
    setBusy(true);
    try {
      await suggestMigrationMapping(mappingId);
      await reload();
    } catch (e) {
      setError(e instanceof Error ? e.message : String(e));
    } finally {
      setBusy(false);
    }
  }

  async function handleCommit() {
    setBusy(true);
    try {
      await commitMigrationRun(migrationRunId);
      await reload();
      onChanged();
    } catch (e) {
      setError(e instanceof Error ? e.message : String(e));
    } finally {
      setBusy(false);
    }
  }

  async function handleSignoff(exceptionId: UUID) {
    const disposition = window.prompt('Catatan disposisi sign-off (wajib diisi):');
    if (!disposition) return;
    setBusy(true);
    try {
      await signoffMigrationException(exceptionId, disposition);
      await reload();
    } catch (e) {
      setError(e instanceof Error ? e.message : String(e));
    } finally {
      setBusy(false);
    }
  }

  async function handleCorrectSignoff(ex: MigrationExceptionRecord) {
    const currentSigner = ex.signed_off_by_email ?? ex.signed_off_by ?? '(tidak diketahui)';
    const disposition = window.prompt(
      `Koreksi Sign-off — ini BUKAN sign-off awal.\n\nPenandatangan saat ini: ${currentSigner} (${formatDateTime(ex.signed_off_at)})\nDisposisi saat ini: ${ex.disposition ?? '-'}\n\nMasukkan disposisi baru untuk mengoreksi penandatangan menjadi akun Anda (sesi terautentikasi saat ini):`,
      ex.disposition ?? ''
    );
    if (!disposition) return;
    setBusy(true);
    try {
      const idempotencyKey = crypto.randomUUID();
      await correctMigrationExceptionSignoff(ex.exception_id, disposition, idempotencyKey);
      await reload();
    } catch (e) {
      setError(e instanceof Error ? e.message : String(e));
    } finally {
      setBusy(false);
    }
  }

  async function handleRollback(checkpointId: UUID) {
    if (!window.confirm('Rollback akan menghapus seluruh data target run ini (tenant clean-target rehearsal, bukan data live). Lanjutkan?')) return;
    setBusy(true);
    try {
      await rollbackMigrationCheckpoint(checkpointId);
      await reload();
      onChanged();
    } catch (e) {
      setError(e instanceof Error ? e.message : String(e));
    } finally {
      setBusy(false);
    }
  }

  const pendingMappings = detail?.mappings.filter((m) => m.status === 'NEEDS_MAPPING_CONFIRMATION') ?? [];

  return (
    <Drawer title="Detail Migration Run" open={open} onClose={onClose}>
      {error && <ErrorNote text={error} />}
      {!detail && !error && <LoadingNote />}
      {detail && (
        <>
          <DrawerField label="Status" value={<StatusBadge status={detail.status} />} />
          <DrawerField label="Source Fingerprint (checksum)" value={<code style={{ fontSize: 11 }}>{detail.source_fingerprint}</code>} />
          <DrawerField
            label="Ringkasan Baris"
            value={`${detail.row_count} total — ${detail.valid_count} valid, ${detail.imported_count} diimpor, ${detail.duplicate_count} duplikat, ${detail.orphan_count} orphan, ${detail.ambiguous_count} ambigu, ${detail.rejected_count} ditolak`}
          />

          {detail.status === 'READY_FOR_COMMIT' && pendingMappings.length > 0 && (
            <HintNote text={`${pendingMappings.length} mapping ambigu belum dikonfirmasi — commit akan diblokir sampai semua dikonfirmasi atau ditolak. AI hanya dapat menyarankan nilai kanonis, tidak pernah menyetujui otomatis.`} />
          )}

          {detail.mappings.length > 0 && (
            <>
              <GroupLabel>Tinjauan Mapping Ambigu (NEEDS_MAPPING_CONFIRMATION)</GroupLabel>
              <table style={tableStyle}>
                <thead>
                  <tr>
                    <th style={thStyle}>Field</th>
                    <th style={thStyle}>Nilai Sumber</th>
                    <th style={thStyle}>Saran AI</th>
                    <th style={thStyle}>Status</th>
                    <th style={thStyle}>Aksi</th>
                  </tr>
                </thead>
                <tbody>
                  {detail.mappings.map((m: MigrationMapping) => (
                    <tr key={m.mapping_id}>
                      <td style={tdStyle}>{m.field_name}</td>
                      <td style={tdStyle}>{m.raw_value}</td>
                      <td style={tdStyle}>{m.candidate_suggestions.length > 0 ? m.candidate_suggestions.join(', ') : (m.ai_suggested ? '(tidak ada saran cocok)' : '—')}</td>
                      <td style={tdStyle}>
                        <StatusBadge status={m.status} />
                        {m.resolved_value && <div style={{ fontSize: 11, color: '#6b7280' }}>→ {m.resolved_value}</div>}
                      </td>
                      <td style={tdStyle}>
                        {m.status === 'NEEDS_MAPPING_CONFIRMATION' && canApprove && (
                          <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap' }}>
                            <Button variant="secondary" disabled={busy} onClick={() => handleSuggest(m.mapping_id)} style={{ padding: '2px 8px', fontSize: 12 }}>
                              Saran AI
                            </Button>
                            {m.candidate_suggestions.map((c) => (
                              <Button key={c} variant="primary" disabled={busy} onClick={() => handleMapping(m.mapping_id, 'CONFIRMED', c)} style={{ padding: '2px 8px', fontSize: 12 }}>
                                Konfirmasi: {c}
                              </Button>
                            ))}
                            <Button variant="danger" disabled={busy} onClick={() => handleMapping(m.mapping_id, 'REJECTED', null)} style={{ padding: '2px 8px', fontSize: 12 }}>
                              Tolak
                            </Button>
                          </div>
                        )}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </>
          )}

          {reconciliation && reconciliation.length > 0 && (
            <>
              <GroupLabel>Rekonsiliasi (agregat deterministik + sampel baris)</GroupLabel>
              <table style={tableStyle}>
                <thead>
                  <tr>
                    <th style={thStyle}>Domain</th>
                    <th style={thStyle}>Metrik</th>
                    <th style={thStyle}>Expected</th>
                    <th style={thStyle}>Actual</th>
                    <th style={thStyle}>Selisih</th>
                    <th style={thStyle}>Status</th>
                  </tr>
                </thead>
                <tbody>
                  {reconciliation.map((r) => (
                    <tr key={r.reconciliation_id}>
                      <td style={tdStyle}>{r.domain}</td>
                      <td style={tdStyle}>{r.metric_name}</td>
                      <td style={tdStyle}>{r.expected_value.toLocaleString('id-ID')}</td>
                      <td style={tdStyle}>{r.actual_value.toLocaleString('id-ID')}</td>
                      <td style={tdStyle}>{r.difference !== 0 ? <strong style={{ color: '#b91c1c' }}>{r.difference.toLocaleString('id-ID')}</strong> : '0'}</td>
                      <td style={tdStyle}>
                        <StatusBadge status={r.status} />
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </>
          )}

          {detail.exceptions.length > 0 && (
            <>
              <GroupLabel>Daftar Pengecualian (Exception Register)</GroupLabel>
              <table style={tableStyle}>
                <thead>
                  <tr>
                    <th style={thStyle}>Kategori</th>
                    <th style={thStyle}>Referensi Sumber</th>
                    <th style={thStyle}>Alasan / Dampak</th>
                    <th style={thStyle}>Sign-off Pemilik</th>
                    <th style={thStyle}>Aksi</th>
                  </tr>
                </thead>
                <tbody>
                  {detail.exceptions.map((ex: MigrationExceptionRecord) => (
                    <tr key={ex.exception_id}>
                      <td style={tdStyle}>{ex.category}</td>
                      <td style={tdStyle}>{ex.source_reference}</td>
                      <td style={tdStyle}>
                        <div>{ex.reason}</div>
                        <div style={{ fontSize: 11, color: '#6b7280' }}>{ex.impact}</div>
                        {ex.disposition && <div style={{ fontSize: 11, color: '#059669' }}>Disposisi: {ex.disposition}</div>}
                      </td>
                      <td style={tdStyle}>
                        <StatusBadge status={ex.owner_signoff_status} />
                        {ex.owner_signoff_status === 'SIGNED_OFF' && (
                          <div style={{ fontSize: 11, color: '#6b7280', marginTop: 4 }}>
                            <div>Ditandatangani: {ex.signed_off_by_email ?? ex.signed_off_by} pada {formatDateTime(ex.signed_off_at)}</div>
                            {ex.correction_count > 0 && (
                              <div style={{ color: '#b45309' }}>
                                Dikoreksi {ex.correction_count}x — penandatangan asli: {ex.original_signed_off_by_email ?? ex.original_signed_off_by} pada {formatDateTime(ex.original_signed_off_at)}
                                {ex.original_disposition && <> (disposisi asli: {ex.original_disposition})</>}
                              </div>
                            )}
                          </div>
                        )}
                      </td>
                      <td style={tdStyle}>
                        {ex.owner_signoff_status === 'PENDING' && canSignoff && (
                          <Button variant="secondary" disabled={busy} onClick={() => handleSignoff(ex.exception_id)} style={{ padding: '2px 8px', fontSize: 12 }}>
                            Sign-off
                          </Button>
                        )}
                        {ex.owner_signoff_status === 'SIGNED_OFF' && canSignoff && (
                          <Button variant="secondary" disabled={busy} onClick={() => handleCorrectSignoff(ex)} style={{ padding: '2px 8px', fontSize: 12 }}>
                            Koreksi Sign-off
                          </Button>
                        )}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </>
          )}

          <GroupLabel>Checkpoint Rollback</GroupLabel>
          {detail.checkpoint ? (
            <DrawerField
              label="Checkpoint"
              value={detail.checkpoint.usable ? 'Tersedia — dapat digunakan untuk rollback' : `Sudah di-rollback pada ${detail.checkpoint.rolled_back_at ? formatDateTime(detail.checkpoint.rolled_back_at) : '-'}`}
            />
          ) : (
            <HintNote text="Belum ada rollback checkpoint untuk run ini (dibuat setelah commit)." />
          )}

          <div style={{ display: 'flex', gap: 8, marginTop: 16, flexWrap: 'wrap' }}>
            {detail.status === 'READY_FOR_COMMIT' && canApprove && (
              <Button variant="primary" disabled={busy || pendingMappings.length > 0} onClick={handleCommit}>
                Commit ke Clean Target
              </Button>
            )}
            {detail.checkpoint?.usable && canApprove && (
              <Button variant="danger" disabled={busy} onClick={() => handleRollback(detail.checkpoint!.checkpoint_id)}>
                Rollback Run Ini
              </Button>
            )}
          </div>
        </>
      )}
    </Drawer>
  );
}
